import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { fetchAllGroupChats, fetchMoreMessages, sendChatMessage } from "../../slices/chat/thunk";
import { updateChatListOrder, clearChatError, clearUnread, clearPendingForChat, updateMessages, setActiveChatId } from "../../slices/chat/reducer";
import { showToast } from "../../slices/toast/reducer";
import { socket } from "../../common/socket";
import GroupChatList from "./components/GroupChatList";
import ChatHeader from "./components/ChatHeader";
import ChatMessages from "./components/ChatMessages";
import ChatInput from "./components/ChatInput";
import MediaViewer from "./components/MediaViewer";
import GroupDetailsDialog from "./components/GroupDetailsDialog";
import DeleteConfirmationDialog from "./components/DeleteConfirmationDialog";
import ScrollToBottomButton from "./components/ScrollToBottomButton";

// Custom Hooks
import { useSocketConnection } from "./hooks/useSocketConnection";
import { useChatMessages } from "./hooks/useChatMessages";
import { useTyping } from "./hooks/useTyping";
import { useFileUpload } from "./hooks/useFileUpload";
import { useScrollManagement } from "./hooks/useScrollManagement";
import { useChatSelection } from "./hooks/useChatSelection";
import { useMessageActions } from "./hooks/useMessageActions";
import { useSocketListeners } from "./hooks/useSocketListeners";
import { useChatHelpers } from "./hooks/useChatHelpers";

function GroupChats() {
  const dispatch = useDispatch();
  const videoPlayingRef = React.useRef(new Set());
  const [newMessage, setNewMessage] = useState("");
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerType, setViewerType] = useState(null);
  const [viewerUrl, setViewerUrl] = useState("");
  const [groupDetailsOpen, setGroupDetailsOpen] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [messageDrafts, setMessageDrafts] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [pendingMessages, setPendingMessages] = useState({});

  useEffect(() => {
    const handlePendingMessage = (message) => {
      const chatId = message.chatId || message.chatRequestId;
      if (String(chatId) !== String(selectedChatId)) {
        setPendingMessages(prev => ({
          ...prev,
          [chatId]: (prev[chatId] || 0) + 1
        }));
      }
    };

    socket.on("newMessage", handlePendingMessage);

    return () => {
      socket.off("newMessage", handlePendingMessage);
    };
  }, [selectedChatId]);

  const {
    groupChats = [],
    currentChat = { messages: [], members: [], page: 1, hasMore: true },
    loading,
    error,
    typingUsers = {},
    unread = {},
    totalGroups = 0,
  } = useSelector((state) => state.Chat || {});

  // Custom Hooks
  const { isSocketConnected } = useSocketConnection(groupChats, selectedChatId);
  const { currentUserId, resolveMemberById, currentChatTypingUsers } = useChatHelpers(
    groupChats,
    currentChat,
    selectedChatId,
    typingUsers
  );
  const {
    chatBoxRef,
    chatEndRef,
    showScrollButton,
    setShowScrollButton,
    scrollToBottom,
    handleScroll: handleScrollWrapper,
    setLastScrollHeight,
    isAtBottomRef
  } = useScrollManagement(currentChat, isTyping);

  const {
    normalizeMessages,
    handleNewMessage,
    handleMessageEdited,
    handleMessagesDeleted,
    currentMessagesRef
  } = useChatMessages(selectedChatId, currentChat, currentUserId, resolveMemberById, isAtBottomRef);

  const { handleTyping, stopTyping } = useTyping(selectedChatId);
  const {
    selectedFile,
    selectedFilePreview,
    setSelectedFile,
    setSelectedFilePreview,
    fileInputRef,
    pickFile,
    onLocalFileChange,
    handleDrop,
    handleUploadFile
  } = useFileUpload(selectedChatId, currentChat);

  const { handleSelectGroup: selectGroupHandler } = useChatSelection(
    selectedChatId,
    currentChat,
    currentUserId,
    messageDrafts,
    normalizeMessages,
    currentMessagesRef
  );

  const {
    deleteDialogOpen,
    setDeleteDialogOpen,
    selectedMessage,
    setSelectedMessage,
    isDeleting,
    editingMessage,
    originalMessage,
    handleDeleteMessage,
    handleConfirmDelete,
    handleEditMessage,
    handleSaveEdit,
    handleCancelEdit
  } = useMessageActions(selectedChatId, currentChat, currentUserId, currentMessagesRef);

  // Socket Listeners
  useSocketListeners({
    selectedChatId,
    currentChat,
    currentUserId,
    handleNewMessage,
    handleMessageEdited,
    handleMessagesDeleted,
    isAtBottomRef,
    chatEndRef,
    setShowScrollButton,
    setNewMessageCount
  });

  // Computed values
  const currentPage = currentChat?.page || 1;
  const hasMoreMessages = currentChat?.hasMore !== false;
  const groupListError = error && !currentChat?._id ? error : null;

  // Handlers
  const handleSelectGroup = useCallback((chat) => {
    const chatId = selectGroupHandler(chat);
    if (chatId) {
      console.log("🎯 [CHAT] Selecting group:", {
        chatId: chatId,
        chatName: chat.name,
        previousChat: selectedChatId,
        timestamp: new Date().toISOString()
      });

      // Clear typing from previous chat
      if (selectedChatId && selectedChatId !== chatId) {
        socket.emit("typing:stop", { chatId: selectedChatId });
      }

      // Set new chat
      setSelectedChatId(chatId);
      dispatch(setActiveChatId(chatId));

      // Clear UI states
      setIsTyping(false);
      setNewMessage(messageDrafts[chatId] || "");
      setSelectedFile(null);
      setSelectedFilePreview("");
      setNewMessageCount(0); // Reset counter when switching chats

      // Stop typing indicators
      stopTyping();

      // ✅ FIXED: Clear unread counts when selecting chat
      dispatch(clearUnread(chatId));
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser"));
        const myId = authUser?.user?._id || authUser?._id;
        if (myId) {
          dispatch(clearPendingForChat({ chatId: chatId, userId: myId }));
        }
      } catch (error) {
        console.error("Error clearing pending on select:", error);
      }

      // ✅ FIXED: Mark messages as read via socket
      socket.emit("markMessagesAsRead", { chatId: chatId });

      // ✅ FIXED: Join the new chat room
      if (socket.connected) {
        console.log("🔗 [CHAT] Joining new chat room:", chatId);
        socket.emit("joinChat", { chatId: chatId });
      }

      console.log("✅ [CHAT] Successfully switched to chat:", chatId);
    }
  }, [selectGroupHandler, messageDrafts, stopTyping, dispatch, selectedChatId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log("🧹 [CHAT] Cleaning up active chat");
      dispatch(setActiveChatId(null));

      // Stop typing in current chat when leaving
      if (selectedChatId) {
        socket.emit("typing:stop", { chatId: selectedChatId });
      }
    };
  }, [dispatch, selectedChatId]);

  useEffect(() => {
    return () => {
      dispatch(setActiveChatId(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedChatId && socket.connected) {
      console.log("🏠 [GroupChats] Joining chat room:", selectedChatId);
      socket.emit("joinChat", { chatId: selectedChatId });

      // Also listen for room joined confirmation
      const handleRoomJoined = (data) => {
        console.log("✅ [GroupChats] Successfully joined room:", data);
      };

      socket.on("roomJoined", handleRoomJoined);

      return () => {
        socket.off("roomJoined", handleRoomJoined);
      };
    }
  }, [selectedChatId, socket.connected]);

  const loadMoreMessages = useCallback(async () => {
    if (loading || !hasMoreMessages || !selectedChatId) return;
    const nextPage = currentPage + 1;
    if (chatBoxRef.current) {
      setLastScrollHeight(chatBoxRef.current.scrollHeight);
    }
    try {
      await dispatch(fetchMoreMessages({
        chatId: selectedChatId,
        page: nextPage,
        limit: 20
      })).unwrap();
    } catch (error) {
      dispatch(showToast({ type: "error", msg: "Failed to load more messages" }));
    }
  }, [selectedChatId, currentPage, hasMoreMessages, loading, dispatch, setLastScrollHeight]);

  const handleScroll = useCallback((event) => {
    handleScrollWrapper(event, loadMoreMessages, loading, hasMoreMessages);

    // Reset new message count and clear unread when user scrolls to bottom
    const chat = event.target;
    const atBottom = chat.scrollHeight - chat.scrollTop - chat.clientHeight < 120;

    if (atBottom) {
      if (newMessageCount > 0) {
        setNewMessageCount(0);
      }

      // ✅ FIXED: Clear unread counts when scrolling to bottom
      if (selectedChatId) {
        socket.emit("markMessagesAsRead", { chatId: selectedChatId });
        try {
          const authUser = JSON.parse(localStorage.getItem("authUser"));
          const myId = authUser?.user?._id || authUser?._id;
          if (myId) {
            dispatch(clearUnread(selectedChatId));
            dispatch(clearPendingForChat({ chatId: selectedChatId, userId: myId }));
          }
        } catch (error) {
          console.error("Error clearing unread on scroll:", error);
        }
      }
    }
  }, [handleScrollWrapper, loadMoreMessages, loading, hasMoreMessages, newMessageCount, selectedChatId, dispatch]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setNewMessage(value);

    if (selectedChatId) {
      setMessageDrafts((prev) => ({
        ...prev,
        [selectedChatId]: value,
      }));
    }

    if (value.trim().length > 0 && !isTyping) {
      setIsTyping(true);
      handleTyping(true);
    } else if (value.trim().length === 0 && isTyping) {
      setIsTyping(false);
      handleTyping(false);
    }
  }, [selectedChatId, isTyping, handleTyping]);

  // Alternative cleaner version
  // ✅ FIXED: handleSendMessage with proper optimistic updates
  const handleSendMessage = useCallback(async () => {
    if (isSending) return;
    setIsSending(true);

    const text = newMessage.trim();
    const chatId = selectedChatId || currentChat?.chatRequestId || currentChat?._id;

    if (!chatId || !text) {
      setIsSending(false);
      return;
    }

    try {
      console.log("📤 Sending message to chat:", chatId);

      // ✅ REMOVED: Manual optimistic update - the thunk handles this in its pending state
      // This was causing duplicate messages to appear

      // Call API to save message (thunk will add optimistic message automatically)
      const result = await dispatch(sendChatMessage({ chatId, content: text })).unwrap();

      if (result) {
        console.log("✅ Message saved, emitting socket event");

        // ✅ FIXED: Emit socket event with proper data
        if (socket.connected) {
          socket.emit("sendMessage", {
            chatId: chatId,
            message: text,
            senderId: currentUserId
          });
          console.log("📤 Socket event emitted successfully");
        } else {
          console.error("❌ Socket not connected, cannot emit event");
        }

        // Update chat list order
        dispatch(updateChatListOrder(chatId));
      }

      setNewMessage("");
      setMessageDrafts((prev) => ({ ...prev, [chatId]: "" }));
    } catch (error) {
      console.log("❌ Failed to send message:", error);
      dispatch(showToast({ type: "error", msg: "Failed to send message" }));

      // Thunk's rejected case will handle cleanup
    } finally {
      setIsSending(false);
      setIsTyping(false);
      handleTyping(false);
    }
  }, [dispatch, isSending, newMessage, selectedChatId, currentChat, handleTyping, currentUserId]);

  const handleSaveEditWrapper = useCallback(async () => {
    const success = await handleSaveEdit(newMessage);
    if (success) {
      setNewMessage("");
      setMessageDrafts((prev) => ({ ...prev, [selectedChatId]: "" }));
    } else {
      setNewMessage(originalMessage);
    }
  }, [handleSaveEdit, newMessage, originalMessage, selectedChatId]);

  // Also update the handleCancelEdit usage:
  const handleCancelEditWrapper = useCallback(() => {
    const clearedValue = handleCancelEdit();
    setNewMessage(clearedValue);
    if (selectedChatId) {
      setMessageDrafts((prev) => ({
        ...prev,
        [selectedChatId]: clearedValue,
      }));
    }
  }, [handleCancelEdit, selectedChatId]);

  // Sync editingMessage with input field - populate input when editing starts
  useEffect(() => {
    if (editingMessage) {
      const messageContent = editingMessage.content || "";
      setNewMessage(messageContent);
      // Also update draft for this chat
      if (selectedChatId) {
        setMessageDrafts((prev) => ({
          ...prev,
          [selectedChatId]: messageContent,
        }));
      }
    }
  }, [editingMessage, selectedChatId]);

  const openViewer = useCallback((url, type) => {
    setViewerUrl(url);
    setViewerType(type);
    setViewerOpen(true);
  }, []);

  // Effects
  useEffect(() => {
    dispatch(fetchAllGroupChats({ page: 1, limit: 50 }));
  }, [dispatch]);

  useEffect(() => {
    if (currentChat?.messages) {
      const normalizedMessages = normalizeMessages(currentChat.messages);
      currentMessagesRef.current = normalizedMessages;
    }
  }, [currentChat?.messages, normalizeMessages, currentMessagesRef]);

  useEffect(() => {
    const overlay = document.getElementById("drop-overlay");
    const input = document.getElementById("message-input");
    if (!overlay || !input) return;

    const show = (e) => {
      e.preventDefault();
      overlay.style.display = "flex";
      input.focus();
    };

    const hide = (e) => {
      e.preventDefault();
      overlay.style.display = "none";
    };

    const preventDefault = (e) => e.preventDefault();

    window.addEventListener("dragenter", show);
    window.addEventListener("dragleave", hide);
    window.addEventListener("drop", hide);
    window.addEventListener("dragover", preventDefault);

    return () => {
      window.removeEventListener("dragenter", show);
      window.removeEventListener("dragleave", hide);
      window.removeEventListener("drop", hide);
      window.removeEventListener("dragover", preventDefault);
    };
  }, []);

  return (
    <div className="page-content chat-page" style={{ height: "calc(100vh - 95px)" }}>
      <Container fluid className="h-100">
        <BreadCrumb title="Group Chats" pageTitle="View Group Chats" />
        <Row className="content-container">
          <Col lg={3} className="h-100">
            <GroupChatList
              groupChats={groupChats}
              totalGroups={totalGroups}
              loading={loading}
              groupListError={groupListError}
              currentChat={currentChat}
              selectedChatId={selectedChatId}
              currentUserId={currentUserId}
              unread={unread}
              typingUsers={typingUsers}
              handleSelectGroup={handleSelectGroup}
              dispatch={dispatch}
              clearChatError={clearChatError}
              fetchAllGroupChats={fetchAllGroupChats}
            />
          </Col>

          <Col lg={9} className="h-100">
            <Card className="h-100 d-flex flex-column shadow-sm">
              <CardBody
                innerRef={chatBoxRef}
                className="flex-grow-1 overflow-auto chat-messages-container p-0"
                style={{
                  maxHeight: "100vh",
                  backgroundColor: "#f8f9fa",
                  position: "relative"
                }}
                onScroll={handleScroll}
              >
                {!currentChat || !currentChat._id ? (
                  <div className="text-center text-muted mt-5">
                    <h5>Select a group to start chatting</h5>
                    <p>All your group messages will appear here.</p>
                  </div>
                ) : (
                  <>
                    <ChatHeader
                      currentChat={currentChat}
                      onGroupDetailsClick={() => setGroupDetailsOpen(true)}
                    />

                    {loading && (
                      <div className="messages-loading">
                        <Spinner size="sm" className="me-2" />
                        Loading older messages...
                      </div>
                    )}

                    {!hasMoreMessages && currentChat.messages.length > 0 && (
                      <div className="no-more-messages">
                        No more messages
                      </div>
                    )}

                    <ChatMessages
                      messages={currentChat.messages || []}
                      currentUserId={currentUserId}
                      openViewer={openViewer}
                      videoPlayingRef={videoPlayingRef}
                      chatEndRef={chatEndRef}
                      currentChatTypingUsers={currentChatTypingUsers}
                      onDeleteMessage={handleDeleteMessage}
                      onEditMessage={handleEditMessage}
                    />
                  </>
                )}
              </CardBody>

              {showScrollButton && currentChat && currentChat._id && (
                <div style={{ position: "relative", width: "100%" }}>
                  <ScrollToBottomButton
                    onClick={() => {
                      scrollToBottom("smooth");
                      setNewMessageCount(0);

                      // Clear unread when clicking scroll to bottom button
                      if (selectedChatId) {
                        socket.emit("markMessagesAsRead", { chatId: selectedChatId });
                        try {
                          const authUser = JSON.parse(localStorage.getItem("authUser"));
                          const myId = authUser?.user?._id || authUser?._id;
                          if (myId) {
                            dispatch(clearUnread(selectedChatId));
                            dispatch(clearPendingForChat({ chatId: selectedChatId, userId: myId }));
                          }
                        } catch (error) {
                          console.error("Error clearing unread on scroll button:", error);
                        }
                      }
                    }}
                    newMessageCount={newMessageCount}
                  />
                </div>
              )}

              {currentChat && currentChat._id && (
                <ChatInput
                  newMessage={newMessage}
                  setNewMessage={setNewMessage}
                  handleInputChange={handleInputChange}
                  handleSendMessage={handleSendMessage}
                  isSending={isSending}
                  selectedFile={selectedFile}
                  selectedFilePreview={selectedFilePreview}
                  setSelectedFile={setSelectedFile}
                  setSelectedFilePreview={setSelectedFilePreview}
                  pickFile={pickFile}
                  fileInputRef={fileInputRef}
                  onLocalFileChange={onLocalFileChange}
                  handleDrop={handleDrop}
                  editingMessage={editingMessage}
                  handleSaveEdit={handleSaveEditWrapper}
                  handleCancelEdit={handleCancelEdit}
                  isEditMode={!!editingMessage}
                  handleUploadFile={handleUploadFile}
                  onCancelEdit={handleCancelEditWrapper}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      <MediaViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        viewerUrl={viewerUrl}
        viewerType={viewerType}
        videoPlayingRef={videoPlayingRef}
      />

      <GroupDetailsDialog
        isOpen={groupDetailsOpen}
        onClose={() => setGroupDetailsOpen(false)}
        currentChat={currentChat}
        showAllMembers={showAllMembers}
        setShowAllMembers={setShowAllMembers}
      />

      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedMessage(null);
        }}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        message={selectedMessage}
        currentUserId={currentUserId}
      />
    </div>
  );
}

export default GroupChats;
